import { DateTime, Duration } from 'luxon';

import {
  AllDaysOfWeek, AllMonthsOfYear, AllTimesOfDay, Days, Months,
} from 'src/util/constants';

// Constants for DateTime creation - using a reference date for time formatting
export const REFERENCE_YEAR = 2024;
export const REFERENCE_MONTH = 1;
export const REFERENCE_DAY = 1;

/**
 * Compares two time objects (with hours, minutes, seconds) for deep equality.
 * @param {object} time1 - First time object
 * @param {object} time2 - Second time object
 * @returns {boolean} True if time objects are equal, false otherwise
 * @private
 */
export const areTimeObjectsEqual = (time1, time2) => {
  if (!time1 && !time2) return true;
  if (!time1 || !time2) return false;

  return (
    time1.hours === time2.hours
    && time1.minutes === time2.minutes
    && time1.seconds === time2.seconds
  );
};

/**
 * Compares two time range objects for deep equality.
 * @param {object} range1 - First time range object with start and finish properties
 * @param {object} range2 - Second time range object with start and finish properties
 * @returns {boolean} True if time ranges are equal, false otherwise
 * @private
 */
const areTimeRangesEqual = (range1, range2) => {
  if (!range1 && !range2) return true;
  if (!range1 || !range2) return false;

  // Check start times
  if (!areTimeObjectsEqual(range1.start, range2.start)) return false;

  // Check finish times
  if (!areTimeObjectsEqual(range1.finish, range2.finish)) return false;

  return true;
};

/**
 * Converts a time object to total seconds for easier arithmetic operations.
 * @param {object} timeObj - Time object with hours, minutes, seconds properties
 * @returns {number} Total seconds, or -1 if invalid time object
 * @private
 */
export const timeObjectToSeconds = (timeObj) => {
  if (!timeObj || typeof timeObj.hours !== 'number') {
    return -1;
  }

  const hours = timeObj.hours || 0;
  const minutes = timeObj.minutes || 0;
  const seconds = timeObj.seconds || 0;

  // Handle 24:00:00 as end of day (86400 seconds)
  if (hours === 24 && minutes === 0 && seconds === 0) {
    return 86400;
  }

  // Validate time bounds
  if (hours < 0 || hours > 24 || minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) {
    return -1;
  }

  const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;

  // Validate that calculated seconds fall within valid range [0, 86400]
  if (totalSeconds < 0 || totalSeconds > 86400) {
    return -1;
  }

  return totalSeconds;
};

/**
 * Validates that a time range object has valid structure and logical time ordering.
 * @param {object} range - Time range object with start and finish properties
 * @returns {boolean} True if range is valid, false otherwise
 * @private
 */
export const isValidTimeRange = (range) => {
  if (!range || typeof range !== 'object') {
    return false;
  }

  const { start, finish } = range;
  if (!start || !finish) {
    return false;
  }

  const startSeconds = timeObjectToSeconds(start);
  const finishSeconds = timeObjectToSeconds(finish);

  if (startSeconds === -1 || finishSeconds === -1) {
    return false;
  }

  // Start time must be before or equal to finish time
  return startSeconds <= finishSeconds;
};

/**
 * Validates that a time object has the required structure and valid time values.
 * @param {object} timeObj - Time object to validate
 * @returns {boolean} True if time object is valid, false otherwise
 * @private
 */
const isValidTimeObject = (timeObj) => {
  if (!timeObj || typeof timeObj !== 'object') {
    return false;
  }

  const { hours, minutes, seconds } = timeObj;

  // hours is required and must be a number
  if (typeof hours !== 'number') {
    return false;
  }

  // minutes and seconds are optional but must be numbers if present
  if (minutes !== undefined && typeof minutes !== 'number') {
    return false;
  }
  if (seconds !== undefined && typeof seconds !== 'number') {
    return false;
  }

  // Use timeObjectToSeconds for comprehensive time validation
  return timeObjectToSeconds(timeObj) !== -1;
};

/**
 * Validates that a time of useable element object has the
 * required structure for time-of-use processing.
 * @param {object} element - The time of useable element object to validate
 * @returns {boolean} True if the element has valid structure, false otherwise
 */
export const isValidTimeOfUse = (element) => {
  if (!element || typeof element !== 'object') {
    return false;
  }

  const { monthsOfYear, daysOfWeek, timesOfDay } = element;

  // Check that all required properties are arrays
  if (!Array.isArray(monthsOfYear) || !Array.isArray(daysOfWeek) || !Array.isArray(timesOfDay)) {
    return false;
  }

  // Check that arrays are non-empty
  if (monthsOfYear.length === 0 || daysOfWeek.length === 0 || timesOfDay.length === 0) {
    return false;
  }

  // Validate monthsOfYear contains only valid month values
  const validMonths = new Set(Months);
  if (!monthsOfYear.every((month) => validMonths.has(month))) {
    return false;
  }

  // Validate daysOfWeek contains only valid day values
  const validDays = new Set(Days);
  if (!daysOfWeek.every((day) => validDays.has(day))) {
    return false;
  }

  // Validate timesOfDay array structure and time object validity
  if (!timesOfDay.every((timeRange) => {
    if (!timeRange || typeof timeRange !== 'object') {
      return false;
    }

    const { start, finish } = timeRange;
    if (!start || !finish) {
      return false;
    }

    return isValidTimeObject(start) && isValidTimeObject(finish);
  })) {
    return false;
  }

  // Validate that each time range has logical ordering (start < finish)
  if (!timesOfDay.every(isValidTimeRange)) {
    return false;
  }

  return true;
};

/**
 * Reduces a set of timeranges into a consolidated set based on the intersection
 * of the timeranges provided.
 * @param {Array} timeRanges - Array of time range objects sorted by start time
 * @returns {Array} Array of merged time range objects
 * @throws {Error} If timeRanges array is not sorted by start time in ascending order
 * @private
 */
export const timerangeReduceByIntersection = (timeRanges) => {
  if (timeRanges.length === 0) {
    return [];
  }

  // Validate that timeRanges array is sorted by start time
  for (let i = 1; i < timeRanges.length; i += 1) {
    const prevStartSeconds = timeObjectToSeconds(timeRanges[i - 1].start);
    const currentStartSeconds = timeObjectToSeconds(timeRanges[i].start);

    if (prevStartSeconds > currentStartSeconds) {
      throw new Error(
        'timeRanges array must be sorted by start time in ascending order. '
        + `Found time range at index ${i - 1} (${timeRanges[i - 1].start.hours}:${timeRanges[i - 1].start.minutes}:${timeRanges[i - 1].start.seconds}) `
        + `starts after time range at index ${i} (${timeRanges[i].start.hours}:${timeRanges[i].start.minutes}:${timeRanges[i].start.seconds})`,
      );
    }
  }

  const merged = [];
  let currentStart = timeObjectToSeconds(timeRanges[0].start);
  let currentFinish = timeObjectToSeconds(timeRanges[0].finish);

  for (let i = 1; i < timeRanges.length; i += 1) {
    const rangeStart = timeObjectToSeconds(timeRanges[i].start);
    const rangeFinish = timeObjectToSeconds(timeRanges[i].finish);

    // Check for gap (absence of overlap/adjacency)
    if (rangeStart > currentFinish) {
      // There's a gap - save current range and start new one
      merged.push({ start: currentStart, finish: currentFinish });
      currentStart = rangeStart;
      currentFinish = rangeFinish;
    } else {
      // No gap (overlapping or adjacent) - merge by extending finish time
      currentFinish = Math.max(currentFinish, rangeFinish);
    }
  }

  // Add the final merged range
  merged.push({ start: currentStart, finish: currentFinish });

  return merged;
};

/**
 * Determines if an array of time ranges provides complete coverage equivalent to "all day"
 * (00:00:00 to 24:00:00). Uses a hybrid approach: fast path for single full-day ranges,
 * and comprehensive merge algorithm for multiple ranges.
 * @param {Array} timeRanges - Array of time range objects with start/finish properties
 * @returns {boolean} True if ranges cover the complete day, false otherwise
 * @example
 * // Returns true - single range covering full day
 * getTimeRangesCoversAllDay([{
 *   start: { hours: 0, minutes: 0, seconds: 0 },
 *   finish: { hours: 24, minutes: 0, seconds: 0 }
 * }]);
 * @example
 * // Returns true - multiple ranges covering full day
 * getTimeRangesCoversAllDay([
 *   { start: { hours: 0, minutes: 0, seconds: 0 },
 *     finish: { hours: 12, minutes: 0, seconds: 0 } },
 *   { start: { hours: 12, minutes: 0, seconds: 0 },
 *     finish: { hours: 24, minutes: 0, seconds: 0 } }
 * ]);
 * @example
 * // Returns true - overlapping ranges covering full day
 * getTimeRangesCoversAllDay([
 *   { start: { hours: 0, minutes: 0, seconds: 0 },
 *     finish: { hours: 14, minutes: 0, seconds: 0 } },
 *   { start: { hours: 10, minutes: 0, seconds: 0 },
 *     finish: { hours: 24, minutes: 0, seconds: 0 } }
 * ]);
 * @example
 * // Returns false - gaps in coverage
 * getTimeRangesCoversAllDay([
 *   { start: { hours: 2, minutes: 30, seconds: 0 },
 *     finish: { hours: 14, minutes: 30, seconds: 0 } },
 *   { start: { hours: 15, minutes: 0, seconds: 0 },
 *     finish: { hours: 16, minutes: 30, seconds: 0 } }
 * ]);
 * @example
 * // Returns false - empty array
 * getTimeRangesCoversAllDay([]);
 * @example
 * // Returns false - invalid time ranges
 * getTimeRangesCoversAllDay([
 *   { start: { hours: 14, minutes: 0, seconds: 0 },
 *     finish: { hours: 12, minutes: 0, seconds: 0 } }
 * ]);
 */
export const getTimeRangesCoversAllDay = (timeRanges) => {
  // Input validation
  if (!Array.isArray(timeRanges) || timeRanges.length === 0) {
    return false;
  }

  // Fast path: Check if single range equals AllTimesOfDay
  if (timeRanges.length === 1) {
    return areTimeRangesEqual(timeRanges[0], AllTimesOfDay);
  }

  // Validate all ranges
  const validRanges = timeRanges.filter(isValidTimeRange);
  if (validRanges.length !== timeRanges.length) {
    return false; // Some ranges were invalid
  }

  // Sort ranges by start time
  const sortedRanges = [...validRanges].sort((a, b) => {
    const aStart = timeObjectToSeconds(a.start);
    const bStart = timeObjectToSeconds(b.start);
    return aStart - bStart;
  });

  // Merge overlapping and adjacent ranges
  const mergedRanges = timerangeReduceByIntersection(sortedRanges);

  // Check if merged result covers exactly 0 to 86400 seconds (full day)
  if (mergedRanges.length !== 1) {
    return false; // Multiple merged ranges means there are gaps
  }

  const singleMerged = mergedRanges[0];
  return singleMerged.start === 0 && singleMerged.finish === 86400;
};

/**
 * Formats a time object to a localized time string.
 * @param {object} timeObj - Time object with hours, minutes, seconds
 * @param {string} locale - The locale for formatting
 * @returns {string} Formatted time string
 */
export const formatTimeObject = (timeObj, locale) => {
  if (!timeObj || typeof timeObj.hours !== 'number') {
    return '';
  }

  const dateTime = DateTime.local(
    REFERENCE_YEAR,
    REFERENCE_MONTH,
    REFERENCE_DAY,
    timeObj.hours,
    timeObj.minutes || 0,
    timeObj.seconds || 0,
    { locale },
  );

  const hourCycle = timeObj.hours === 24 ? 'h24' : 'h23';
  return dateTime.toLocaleString({
    hour: '2-digit',
    minute: '2-digit',
    hourCycle,
  });
};

/**
 * Determines if a time-of-useable element represents a "simple" or unrestricted condition.
 * A time of useable element (clause, charge) is considered simple when it applies to all months,
 * all days, and all times, effectively meaning there are no time-based restrictions.
 * @param {object} element - The time-of-use element object to evaluate
 * @param {Array} element.monthsOfYear - Array of month constants
 *   (e.g., [MONTH_JANUARY, MONTH_FEBRUARY, ...])
 * @param {Array} element.daysOfWeek - Array of day constants
 *   (e.g., [DAY_MONDAY, DAY_TUESDAY, ...])
 * @param {Array} element.timesOfDay - Array of time range objects
 *   with start/finish properties
 * @param {boolean} [element.ignorePublicHolidays=false] - Whether public
 *   holidays should be ignored in day matching
 * @returns {boolean} True if the element applies to all times (is simple), false otherwise
 * @example
 * // Returns true - applies to all times
 * isTimeOfUseSimple({
 *   monthsOfYear: AllMonthsOfYear,
 *   daysOfWeek: AllDaysOfWeek,
 *   timesOfDay: [AllTimesOfDay],
 *   ignorePublicHolidays: false
 * });
 * @example
 * // Returns true - ignores public holidays, days match when concatenated
 * isTimeOfUseSimple({
 *   monthsOfYear: AllMonthsOfYear,
 *   daysOfWeek: [DAY_MONDAY, DAY_TUESDAY, DAY_WEDNESDAY, DAY_THURSDAY,
 *     DAY_FRIDAY, DAY_SATURDAY, DAY_SUNDAY],
 *   timesOfDay: [AllTimesOfDay],
 *   ignorePublicHolidays: true
 * });
 * @example
 * // Returns false - restricted to specific months
 * isTimeOfUseSimple({
 *   monthsOfYear: [MONTH_JANUARY, MONTH_FEBRUARY],
 *   daysOfWeek: AllDaysOfWeek,
 *   timesOfDay: [AllTimesOfDay],
 *   ignorePublicHolidays: false
 * });
 */
export const isSimple = (element) => {
  const {
    monthsOfYear, daysOfWeek, timesOfDay, ignorePublicHolidays,
  } = element;

  const appliesAllMonths = (new Set(AllMonthsOfYear)).isSubsetOf(new Set(monthsOfYear));
  const appliesAllDays = (new Set(ignorePublicHolidays
    ? AllDaysOfWeek.slice(0, 7) : AllDaysOfWeek)).isSubsetOf(new Set(daysOfWeek));

  const appliesAllHours = getTimeRangesCoversAllDay(timesOfDay);

  return appliesAllMonths && appliesAllDays && appliesAllHours;
};

/**
 * Converts a time-of-day object to a Luxon Duration object for time calculations and formatting.
 * This function transforms time objects used throughout the time-of-use system into Duration
 * objects that can be used for arithmetic operations, comparisons, and formatting.
 * @param {object} timeOfDay - Time object with time components
 * @param {number} timeOfDay.hours - Hours component (0-24, where 24 represents end of day)
 * @param {number} [timeOfDay.minutes=0] - Minutes component (0-59)
 * @param {number} [timeOfDay.seconds=0] - Seconds component (0-59)
 * @param {number} [timeOfDay.nanos] - Nanoseconds component (converted to milliseconds)
 * @returns {Duration} Luxon Duration object representing the time-of-day
 * @example
 * // Convert morning time to Duration
 * const morningTime = { hours: 9, minutes: 30, seconds: 0 };
 * const duration = timeOfDayToDuration(morningTime);
 * console.log(duration.toFormat('hh:mm')); // "09:30"
 * @example
 * // Convert time with seconds to Duration
 * const preciseTime = { hours: 12, minutes: 15, seconds: 45 };
 * const duration = timeOfDayToDuration(preciseTime);
 * console.log(duration.hours); // 12
 * console.log(duration.minutes); // 15
 * console.log(duration.seconds); // 45
 * @example
 * // Convert time with nanoseconds to Duration (nanoseconds converted to milliseconds)
 * const timeWithNanos = { hours: 8, minutes: 0, seconds: 0, nanos: 500000000 };
 * const duration = timeOfDayToDuration(timeWithNanos);
 * console.log(duration.milliseconds); // 500
 * @example
 * // Convert end-of-day time (24:00:00)
 * const endOfDay = { hours: 24, minutes: 0, seconds: 0 };
 * const duration = timeOfDayToDuration(endOfDay);
 * console.log(duration.hours); // 24
 * @example
 * // Handle missing optional properties (defaults to 0)
 * const basicTime = { hours: 10 };
 * const duration = timeOfDayToDuration(basicTime);
 * console.log(duration.minutes); // 0
 * console.log(duration.seconds); // 0
 */
export const timeOfDayToDuration = (timeOfDay) => Duration.fromObject({
  hours: timeOfDay.hours,
  minutes: timeOfDay.minutes,
  seconds: timeOfDay.seconds,
  milliseconds: timeOfDay.nanos && (timeOfDay.nanos / 1000000),
});

/**
 * Checks if multiple time-of-useable elements share identical time-of-use conditions
 * @param {Array} elementNodes - Array of element nodes.
 * @returns {boolean} True if multiple time of useable elements exist and share identical conditions
 */
export const hasSharedConditions = (elementNodes) => {
  if (!elementNodes || elementNodes.length <= 1) {
    return false;
  }

  const extractConditions = (element) => ({
    ignoreDaylightSavings: element.ignoreDaylightSavings,
    ignorePublicHolidays: element.ignorePublicHolidays,
    timezone: element.timezone,
    publicHolidayRegion: element.publicHolidayRegion,
  });

  const allConditions = elementNodes.map(extractConditions);

  // Compare all conditions against the first one
  const firstCondition = allConditions[0];
  const allIdentical = allConditions.every(
    (condition) => condition.ignoreDaylightSavings === firstCondition.ignoreDaylightSavings
    && condition.ignorePublicHolidays === firstCondition.ignorePublicHolidays
    && condition.timezone === firstCondition.timezone
    && condition.publicHolidayRegion === firstCondition.publicHolidayRegion,
  );

  return allIdentical;
};
